// Small "i" icon that reveals a plain-English explanation on hover/click.
// The tooltip renders in a portal at document.body with fixed positioning —
// parent containers with overflow-hidden (scorecards, table cells) were
// clipping it when it was absolutely positioned inline. Position is clamped
// to the viewport and flips above the icon when there's no room below.

import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

const TIP_WIDTH = 300;

export default function InfoTip({ title, children, align: _ignored }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const tipRef = useRef(null);
  const [style, setStyle] = useState(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current || !tipRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const tipH = tipRef.current.offsetHeight;
    let left = r.left + r.width / 2 - TIP_WIDTH / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - TIP_WIDTH - 8));
    const below = r.bottom + 8 + tipH <= window.innerHeight - 8;
    const top = below ? r.bottom + 8 : Math.max(8, r.top - 8 - tipH);
    setStyle({ position: 'fixed', top, left, width: TIP_WIDTH, zIndex: 9999 });
  }, [open]);

  return (
    <span className="relative inline-flex align-middle">
      <button
        ref={btnRef}
        type="button"
        aria-label={title ? `What does ${title} mean?` : 'More info'}
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="text-gray-400 hover:text-gray-600 focus:outline-none"
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {open && createPortal(
        <span
          ref={tipRef}
          style={style || { position: 'fixed', top: -9999, left: -9999, width: TIP_WIDTH }}
          className="block bg-gray-900 text-gray-100 text-xs rounded-lg p-3 shadow-xl leading-relaxed normal-case font-normal tracking-normal text-left whitespace-normal pointer-events-none"
        >
          {title && <span className="block font-semibold text-white mb-1">{title}</span>}
          {children}
        </span>,
        document.body
      )}
    </span>
  );
}
